import { S } from '../regureStyles';
import AreaChart from './AreaChart';
import DonutChart from './DonutChart';

const Chart = ({ t, liveStatusData, totalClaimsLive, liveVolumeData, volumeRangeLabel }) => {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Donut Chart */}
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('dashboard.claimsByStatus')}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('dashboard.currentDistribution')}</div>
          </div>
          {/* The donut is the CURRENT distribution across every case the
              tenant has — the aggregation behind it carries no date filter
              at all. It was chipped "YTD 2026", a hardcoded year that was
              both stale and a claim about a window this figure never
              applied. */}
          <span style={{ fontSize: 11, fontWeight: 600, color: S.textMuted }}>{t('dashboard.allTime')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <DonutChart data={liveStatusData} total={totalClaimsLive} />
          <div style={{ flex: 1 }}>
            {liveStatusData.map(d => (
              <div key={d.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: S.textSecondary }}>{d.label}</span>
                </div>
                <span style={{ fontSize: 12, fontWeight: 600, color: S.textPrimary }}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Area Chart */}
      <div style={{ background: '#fff', border: `1px solid ${S.borderLight}`, borderRadius: 16, padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.textPrimary }}>{t('dashboard.claimsVolume')}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>{t('dashboard.monthlyIntake')}</div>
          </div>
          {/* Derived from the rendered series, not asserted. Absent when
              there is no series to describe. */}
          {volumeRangeLabel && (
            <span style={{ fontSize: 11, fontWeight: 600, color: S.textMuted }}>{volumeRangeLabel}</span>
          )}
        </div>
        <div style={{ height: 140, marginBottom: 8 }}>
          {/* describePoint is passed in rather than built inside the chart so
              the chart stays a chart: it knows how to plot a series, not what
              the series is called. The label uses the tenant's own word for a
              case via the {{claim}}/{{claims}} interpolation variables, and
              {{count}} drives i18next's plural selection. */}
          <AreaChart
            data={liveVolumeData}
            color={S.burgundy}
            describePoint={d => t('dashboard.volumeTooltip', {
              count: Number(d.value) || 0,
              month: d.year ? `${d.month} ${d.year}` : String(d.month),
            })}
          />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {liveVolumeData
            .filter((_, i) => i % 2 === 0)
            .map(d => (
              <span key={`${d.year ?? ''}-${d.month}`} style={{ fontSize: 10, color: S.textMuted, fontWeight: 600 }}>
                {d.month}
              </span>
            ))}
        </div>
        {/* Summary metrics — computed from the same liveVolumeData the chart
            renders, not from hardcoded values. Renders '—' when there's no
            data so a zero-claim tenant sees an honest empty state rather
            than a fabricated 322 / 26.8 / 38 (Jan). */}
        {(() => {
          const vals = (liveVolumeData || []).map(d => Number(d.value) || 0);
          const total = vals.reduce((a, b) => a + b, 0);
          const avg = vals.length ? Math.round((total / vals.length) * 10) / 10 : null;
          const peakIdx = vals.length ? vals.indexOf(Math.max(...vals)) : -1;
          const peakMonth = peakIdx >= 0 ? liveVolumeData[peakIdx].month : null;
          const peakVal = peakIdx >= 0 ? vals[peakIdx] : null;
          return (
            <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
              <div>
                <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600 }}>{t('dashboard.avgMonth')}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>{avg == null ? '—' : avg}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600 }}>{t('dashboard.peakMonth')}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: S.burgundy }}>{peakMonth == null ? '—' : `${peakVal} (${peakMonth})`}</div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: S.textMuted, fontWeight: 600 }}>{t('dashboard.ytdTotal')}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>{total || '—'}</div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default Chart;
